import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

import { BASE_URL } from '../api/apiCaller';
import { accessToken } from '../redux/auth/service';
import {
    FetchMessagesErrorSchema,
    FetchMessagesResponseSchema,
    GetConversationResponseSchema,
    MessageSchema
} from '../types/chat';

export interface FetchMessagesParams {
    conversationId: number;
    entityId: string | null;
    page: number;
    resultsPerPage: number;
    query?: string;
}

interface UseChatWebSocketOptions {
    conversationId: number | null;
    entityId: string | null;
    onNewMessage?: (message: MessageSchema) => void;
    onMessageDeleted?: (conversationId: number, messageId: number) => void;
    onReactionUpdate?: (
        conversationId: number,
        messageId: number,
        reactions: { emoji: string; users: string[] }[]
    ) => void;
    onConnectionStatusChange?: (connected: boolean) => void;
}

export interface UseChatWebSocketReturn {
    fetchMessages: (
        params: FetchMessagesParams
    ) => Promise<GetConversationResponseSchema>;
    isConnected: boolean;
}

const FETCH_MESSAGES_TIMEOUT_MS = 15000;

/**
 * Manages the WebSocket connection for a chat conversation.
 * Handles both request/response message fetching and real-time push events.
 * fetchMessages rejects immediately if the socket is not connected,
 * allowing callers to fall back to REST transparently.
 */
export const useChatWebSocket = ({
    conversationId,
    entityId,
    onNewMessage,
    onMessageDeleted,
    onReactionUpdate,
    onConnectionStatusChange
}: UseChatWebSocketOptions): UseChatWebSocketReturn => {
    const socketRef = useRef<Socket | null>(null);
    const pendingRequestsRef = useRef<
        Map<
            string,
            {
                resolve: (value: GetConversationResponseSchema) => void;
                reject: (error: unknown) => void;
                timeout: ReturnType<typeof setTimeout>;
            }
        >
    >(new Map());
    const [isConnected, setIsConnected] = useState(false);

    const newMessageCallbackRef = useRef(onNewMessage);
    const messageDeletedCallbackRef = useRef(onMessageDeleted);
    const reactionUpdateCallbackRef = useRef(onReactionUpdate);
    const connectionStatusCallbackRef = useRef(onConnectionStatusChange);

    newMessageCallbackRef.current = onNewMessage;
    messageDeletedCallbackRef.current = onMessageDeleted;
    reactionUpdateCallbackRef.current = onReactionUpdate;
    connectionStatusCallbackRef.current = onConnectionStatusChange;

    const handleConnectionChange = useCallback((connected: boolean) => {
        setIsConnected(connected);
        connectionStatusCallbackRef.current?.(connected);
    }, []);

    useEffect(() => {
        if (!conversationId || !entityId) {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            return;
        }

        const token = accessToken();
        if (!token) {
            console.warn('No access token available for WebSocket connection');
            return;
        }

        const socket = io(BASE_URL, {
            path: '/waggle-ws/api/v1/chat/ws',
            transports: ['polling', 'websocket'],
            auth: { token, jwt: token },
            query: {
                conversation_id: conversationId,
                entity_id: entityId,
                token
            },
            extraHeaders: { Authorization: `Bearer ${token}` },
            withCredentials: true,
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
            randomizationFactor: 0.5,
            forceNew: false,
            timeout: 20000
        });

        socketRef.current = socket;

        const roomName = `conversation_${conversationId}`;

        socket.on('connect', () => {
            socket.emit('join', roomName);
            handleConnectionChange(true);
        });

        socket.on('reconnect', () => {
            socket.emit('join', roomName);
            handleConnectionChange(true);
        });

        socket.on('connect_error', (error: Error) => {
            console.warn('WebSocket connection error:', error);
            handleConnectionChange(false);
        });

        socket.on('disconnect', () => {
            handleConnectionChange(false);
            pendingRequestsRef.current.forEach(({ timeout, reject }) => {
                clearTimeout(timeout);
                reject(new Error('WebSocket disconnected'));
            });
            pendingRequestsRef.current.clear();
        });

        socket.on(
            'fetch_messages_response',
            (data: FetchMessagesResponseSchema) => {
                const pending = pendingRequestsRef.current.get(data.requestId);
                if (pending) {
                    clearTimeout(pending.timeout);
                    pendingRequestsRef.current.delete(data.requestId);
                    pending.resolve({
                        data: {
                            id: data.id,
                            name: data.name ?? '',
                            messages: data.messages ?? [],
                            nextCursor: Boolean(data.nextCursor),
                            pageFull: Boolean(data.pageFull)
                        },
                        status: 'ok'
                    });
                }
            }
        );

        socket.on('fetch_messages_error', (data: FetchMessagesErrorSchema) => {
            const pending = pendingRequestsRef.current.get(data.requestId);
            if (pending) {
                clearTimeout(pending.timeout);
                pendingRequestsRef.current.delete(data.requestId);
                pending.reject(
                    data.error ?? new Error('fetch_messages failed')
                );
            }
        });

        socket.on('chat_message', (data: any, ack?: () => void) => {
            const receivedConversationId =
                data.conversationId || data.conversation_id;
            const message = data.message || data;

            if (receivedConversationId !== conversationId) {
                ack?.();
                return;
            }

            try {
                if (message?.id && message?.conversationId) {
                    newMessageCallbackRef.current?.(message as MessageSchema);
                }
                ack?.();
            } catch (error) {
                console.error('Error processing chat message:', error);
            }
        });

        socket.on('chat_message_deleted', (data: any, ack?: () => void) => {
            const receivedConversationId =
                data.conversationId || data.conversation_id;
            const messageId = data.messageId || data.message_id;

            if (receivedConversationId !== conversationId) {
                ack?.();
                return;
            }

            try {
                if (messageId) {
                    messageDeletedCallbackRef.current?.(
                        receivedConversationId,
                        messageId
                    );
                }
                ack?.();
            } catch (error) {
                console.error('Error processing message deletion:', error);
            }
        });

        socket.on('reaction_update', (data: any) => {
            const receivedConversationId = data.conversationId;
            const messageId = data.messageId;
            const reactions = data.reactions;

            if (receivedConversationId !== conversationId) {
                return;
            }

            try {
                if (messageId !== undefined && Array.isArray(reactions)) {
                    reactionUpdateCallbackRef.current?.(
                        receivedConversationId,
                        messageId,
                        reactions
                    );
                }
            } catch (error) {
                console.error('Error processing reaction update:', error);
            }
        });

        return () => {
            // pendingRequestsRef.current is a stable Map instance (never reassigned), so
            // capturing it here is safe despite the react-hooks/exhaustive-deps warning.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const pending = pendingRequestsRef.current;
            pending.forEach(({ timeout, reject }) => {
                clearTimeout(timeout);
                reject(new Error('WebSocket disconnected'));
            });
            pending.clear();
            if (socketRef.current) {
                socketRef.current.emit('leave', roomName);
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            setIsConnected(false);
        };
    }, [conversationId, entityId, handleConnectionChange]);

    const fetchMessages = useCallback(
        (
            params: FetchMessagesParams
        ): Promise<GetConversationResponseSchema> => {
            const socket = socketRef.current;
            if (!socket?.connected) {
                return Promise.reject(new Error('WebSocket not connected'));
            }

            const requestId = `req_${Date.now()}_${Math.random()
                .toString(36)
                .slice(2, 11)}`;

            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    pendingRequestsRef.current.delete(requestId);
                    reject(
                        new Error(
                            `fetch_messages timeout (${FETCH_MESSAGES_TIMEOUT_MS}ms)`
                        )
                    );
                }, FETCH_MESSAGES_TIMEOUT_MS);

                pendingRequestsRef.current.set(requestId, {
                    resolve,
                    reject,
                    timeout
                });

                socket.emit('fetch_messages', {
                    requestId,
                    conversation_id: params.conversationId,
                    entity_id: params.entityId,
                    page: params.page,
                    results_per_page: params.resultsPerPage,
                    query: params.query ?? ''
                });
            });
        },
        []
    );

    return { fetchMessages, isConnected };
};

export default useChatWebSocket;
